import { execSync, execFileSync } from 'node:child_process'
import { readFile } from 'node:fs/promises'
import { join } from 'node:path'
import chalk from 'chalk'
import ora from 'ora'
import type { DetectedProvider, ProgressCallback, SessionEvent } from '@tonty/core'

function checkNodeVersion(): string | null {
  const major = parseInt(process.version.slice(1).split('.')[0]!, 10)
  if (major < 18) {
    return `Node.js ${major} is too old. This platform requires Node.js 18 or newer.\n  Install a newer version: https://nodejs.org`
  }
  if (major < 20) {
    return `Node.js ${major} is below the recommended version (20+). Some scenarios may not work correctly.\n  Consider upgrading: https://nodejs.org`
  }
  return null
}

interface PlatformInfo {
  platform: NodeJS.Platform
  isWSL: boolean
  wslDistro: string | null
  isMac: boolean
  isLinux: boolean
}

function detectPlatform(): PlatformInfo {
  const platform = process.platform
  let isWSL = false
  let wslDistro: string | null = null

  if (platform === 'linux') {
    try {
      const release = execFileSync('uname', ['-r'], { stdio: ['ignore', 'pipe', 'ignore'] })
        .toString()
        .toLowerCase()
      if (release.includes('microsoft') || release.includes('wsl')) {
        isWSL = true
        try {
          const distroRaw = execFileSync('cat', ['/etc/os-release'], {
            stdio: ['ignore', 'pipe', 'ignore'],
          }).toString()
          const match = distroRaw.match(/^PRETTY_NAME="?([^"\n]+)"?/m)
          if (match) wslDistro = match[1]!
        } catch {
          // best-effort
        }
      }
    } catch {
      // uname unavailable (rare on Linux); assume not WSL
    }
  }

  return {
    platform,
    isWSL,
    wslDistro,
    isMac: platform === 'darwin',
    isLinux: platform === 'linux' && !isWSL,
  }
}
import {
  discoverBuiltInScenarios,
  detectInstalledProviders,
  getLauncher,
  SessionManager,
  listSessions,
  loadSession,
  toStoredSession,
  saveSession,
  type Session,
  type ScenarioConfig,
} from '@tonty/core'
import {
  SessionServer,
  decodeSessionCode,
  isCloudSessionCode,
  downloadSession,
  downloadSessionFromCloud,
  uploadSession,
  getWorkerUrl,
} from '@tonty/core/network'
import { showWelcome } from '../ui/display.js'

function banner(): void {
  showWelcome()
}

function nodePreflight(): void {
  const warning = checkNodeVersion()
  if (warning) {
    if (warning.includes('too old')) {
      console.log(chalk.red('✗ Node.js version check failed'))
      console.log(chalk.red(`  ${warning}`))
      process.exit(1)
    }
    console.log(chalk.yellow('⚠ Node.js version warning'))
    console.log(chalk.yellow(`  ${warning}`))
    console.log()
  }

  if (process.platform === 'win32') {
    console.log(chalk.red.bold('✗ Windows non supporté'))
    console.log()
    console.log(
      chalk.white('Cet outil nécessite un environnement Linux pour fonctionner correctement.'),
    )
    console.log(
      chalk.white('Les LLM, CLI agents, PTY, et scripts bash utilisés ne sont pas compatibles'),
    )
    console.log(chalk.white('avec Windows natif.'))
    console.log()
    console.log(chalk.bold('Solutions :'))
    console.log(
      chalk.white('  1. WSL2 (recommandé) — https://learn.microsoft.com/fr-fr/windows/wsl/install'),
    )
    console.log(
      chalk.white('     Installez Ubuntu puis exécutez la commande dans le terminal WSL.'),
    )
    console.log(chalk.white('  2. VM Linux — https://ubuntu.com/download/desktop'))
    console.log(chalk.white('     Machine virtuelle Ubuntu avec VirtualBox ou VMware.'))
    console.log(chalk.white('  3. Git Bash — https://git-scm.com'))
    console.log(chalk.white('     Solution partielle, certains outils peuvent ne pas fonctionner.'))
    console.log()
    console.log(
      chalk.gray(
        'Les environnements Linux et macOS offrent les performances et outils nécessaires',
      ),
    )
    console.log(chalk.gray('pour les tests techniques avec agents IA.'))
    console.log()
    console.log(
      chalk.dim('PS : En tant que dev, vous devriez utiliser moins Windows et plus Linux. 🤷'),
    )
    process.exit(1)
  }

  const info = detectPlatform()
  if (info.isWSL) {
    console.log(
      chalk.gray(
        `ℹ  WSL detected${info.wslDistro ? ` (${info.wslDistro})` : ''} — Linux subsystem running.`,
      ),
    )
    console.log()
  }
}

export async function listCommand(): Promise<void> {
  banner()
  const scenarios = await discoverBuiltInScenarios()

  if (scenarios.length === 0) {
    console.log(chalk.yellow('No scenarios found.'))
    return
  }

  console.log(chalk.bold('Available Scenarios:'))
  console.log()

  for (const s of scenarios) {
    const diffColor =
      s.config.difficulty === 'hard'
        ? chalk.red
        : s.config.difficulty === 'medium'
          ? chalk.yellow
          : chalk.green
    console.log(
      `  ${chalk.bold(s.name.padEnd(30))} ${diffColor(s.config.difficulty.padEnd(8))} ${chalk.gray(s.config.estimated_time.padEnd(10))} ${s.config.description}`,
    )
  }

  console.log()
  console.log(chalk.gray(`Run: ${chalk.white('tonty-tech-interviews start <scenario>')}`))
  console.log()
}

export async function startCommand(
  scenarioName: string,
  options: {
    provider?: string
    model?: string
    permissionMode?: string
    disallowedTools?: string
    skipSetup?: boolean
  } = {},
): Promise<void> {
  banner()
  nodePreflight()

  const scenarios = await discoverBuiltInScenarios()
  const match = scenarios.find((s) => s.name === scenarioName)

  if (!match) {
    console.log(chalk.red(`Scenario not found: ${scenarioName}`))
    console.log(
      chalk.gray(`Run ${chalk.white('tonty-tech-interviews list')} to see available scenarios.`),
    )
    process.exit(1)
  }

  const config = match.config

  // Detect provider
  const providers = await detectInstalledProviders()
  if (providers.length === 0) {
    console.log(chalk.red('No AI coding providers detected.'))
    console.log()
    console.log(chalk.gray('Your AI tool may not be on PATH. Common fixes:'))
    console.log(chalk.gray('  1. Verify npm global bin is on PATH:'))
    console.log(chalk.white(`     export PATH="$(npm prefix -g)/bin:$PATH"`))
    console.log(chalk.gray('  2. Install an AI coding tool:'))
    console.log(chalk.white('     npm install -g freebuff'))
    console.log(chalk.white('     npm install -g cline'))
    console.log(chalk.white('     npm install -g @charmland/crush'))
    console.log(chalk.white('     npm install -g @earendil-works/pi-coding-agent'))
    console.log(chalk.white('     npm install -g @openai/codex'))
    console.log(chalk.white('     curl -fsSL https://x.ai/cli/install.sh | bash'))
    console.log(chalk.gray('  3. Or run: ') + chalk.white('npx freebuff'))
    console.log()
    console.log(
      chalk.gray('If installed but not detected, use --provider to force a specific tool.'),
    )
    process.exit(1)
  }

  const launcherName = await selectProvider(providers, options.provider, 'Pick')

  const launcher = getLauncher(launcherName)
  if (!launcher) {
    console.log(chalk.red(`Provider not found: ${launcherName}`))
    console.log(chalk.gray(`Available: ${providers.map((p) => p.launcher.name).join(', ')}`))
    process.exit(1)
  }

  const providerInfo = providers.find((p) => p.launcher.name === launcherName)
  console.log(
    chalk.gray(`Provider: ${chalk.white(launcher.displayName)} ${providerInfo?.version ?? ''}`),
  )
  console.log(
    chalk.gray(
      `Scenario: ${chalk.white(config.name)} (${config.difficulty}, ~${config.estimated_time})`,
    ),
  )
  console.log()

  const manager = new SessionManager(launcher)
  const spinner = ora('Preparing session...').start()

  const onProgress: ProgressCallback = (stage: string) => {
    spinner.text = stage
  }

  try {
    const { session } = await manager.createSession(config, undefined, onProgress, {
      skipSetup: options.skipSetup,
    })

    spinner.succeed('Session ready')
    console.log()
    console.log(chalk.bold('Interview Briefing:'))
    console.log(chalk.gray('─'.repeat(40)))
    console.log(config.briefing.trim())
    console.log(chalk.gray('─'.repeat(40)))
    console.log()
    console.log(chalk.gray(`Session ID: ${session.id}`))
    console.log(chalk.gray(`Workdir:   ${session.workdir}`))
    console.log()

    console.log(chalk.gray(`Démarrage de ${launcher.displayName}...`))
    console.log()

    const result = await manager.launchProvider(session, config, {
      model: options.model,
      permissionMode: options.permissionMode as
        | 'default'
        | 'plan'
        | 'acceptEdits'
        | 'bypassPermissions'
        | undefined,
      disallowedTools: options.disallowedTools ? options.disallowedTools.split(',') : undefined,
    })

    if (result.exitCode === 0) {
      console.log(chalk.green('Session completed.'))
    } else {
      console.log(chalk.yellow(`Session ended with exit code ${result.exitCode}.`))
    }
    await displayScore(session.id)
  } catch (err) {
    spinner.fail('Failed to start session')
    handleError(err)
  }
}

export async function hostCommand(
  scenarioName: string,
  options: { provider?: string; port?: number; cloud?: boolean } = {},
): Promise<void> {
  banner()
  nodePreflight()

  const scenarios = await discoverBuiltInScenarios()
  const match = scenarios.find((s) => s.name === scenarioName)

  if (!match) {
    console.log(chalk.red(`Scenario not found: ${scenarioName}`))
    process.exit(1)
  }

  const config = match.config
  const providers = await detectInstalledProviders()

  if (providers.length === 0) {
    console.log(chalk.yellow('Warning: No AI coding providers detected on this machine.'))
    console.log(chalk.gray('You can still create a session, but launching requires a provider.'))
    console.log()
  }

  let launcherName = options.provider
  if (!launcherName && providers.length > 0) {
    launcherName = providers[0]!.launcher.name
  }

  const launcher = launcherName ? getLauncher(launcherName) : undefined

  const manager = launcher ? new SessionManager(launcher) : undefined
  const spinner = ora('Preparing session...').start()

  try {
    if (!manager) {
      spinner.fail('No provider available for hosting')
      process.exit(1)
    }

    const { session } = await manager.createSession(config, undefined, (stage) => {
      spinner.text = stage
    })

    spinner.succeed('Session prepared')

    if (options.cloud) {
      // Cloud mode: upload to Cloudflare Worker
      const tarball = join(session.workdir, '..', `${session.id}.tar.gz`)
      execSync(`tar czf "${tarball}" -C "${session.workdir}" .`, { stdio: 'pipe' })
      const systemPrompt = await readFile(session.systemPromptPath, 'utf-8')

      const uploadSpinner = ora('Uploading to cloud...').start()
      const workerUrl = getWorkerUrl()
      const result = await uploadSession(
        workerUrl,
        {
          scenarioName: config.name,
          type: config.type,
          difficulty: config.difficulty,
          estimatedTime: config.estimated_time,
          briefing: config.briefing,
          setupCommands: config.setup,
          repo: config.repo,
          commit: config.commit,
          patch: config.patch,
          delete_files: config.delete_files,
        },
        systemPrompt,
        tarball,
        (stage) => {
          uploadSpinner.text = stage
        },
      )

      uploadSpinner.succeed('Session uploaded to cloud')
      console.log()
      console.log(chalk.bold('Session Code: ') + chalk.cyan.bold(result.code))
      console.log(chalk.gray(`  Expires: ${new Date(result.expiresAt).toLocaleString()}`))
      console.log()
      console.log(chalk.gray('Share this code with the candidate.'))
      console.log(
        chalk.gray('They run: ') + chalk.white('tonty-tech-interviews join ' + result.code),
      )
      console.log()
      console.log(chalk.gray('Or send them to the web app to check in with this code.'))
      console.log()
    } else {
      // LAN mode: start local server
      const server = new SessionServer()
      const { code, port, host } = await server.start(session, config, options.port)

      console.log()
      console.log(chalk.bold('Session Code: ') + chalk.cyan.bold(code))
      console.log(chalk.gray(`  Host: ${host}:${port}`))
      console.log()
      console.log(chalk.gray('Share this code with the candidate.'))
      console.log(chalk.gray('They run: ') + chalk.white('tonty-tech-interviews join ' + code))
      console.log()
      console.log(chalk.gray('Press Ctrl+C when the session is complete.'))
      console.log()

      // Wait for Ctrl+C
      await new Promise<void>((resolve) => {
        process.on('SIGINT', () => {
          console.log(chalk.gray('\nStopping server...'))
          resolve()
        })
      })

      await server.stop()
    }

    console.log(chalk.green('Session ended.'))
  } catch (err) {
    spinner.fail('Failed to host session')
    handleError(err)
  }
}

/**
 * POST a heartbeat to the relay with retries.
 *
 * The heartbeat is what sets `startedAt` and extends the session TTL on the
 * relay. It used to be a single fire-and-forget fetch: one network hiccup
 * and the admin dashboard would label the session as a bare "check-in" even
 * after the candidate finished. Retrying with backoff makes that state
 * reachable only if the relay is truly unreachable.
 */
async function sendHeartbeatWithRetry(workerUrl: string, apiCode: string): Promise<boolean> {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(`${workerUrl}/sessions/${apiCode}/heartbeat`, {
        method: 'POST',
        signal: AbortSignal.timeout(15000),
      })
      if (res.ok) return true
    } catch {
      // fall through to retry
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
  }
  return false
}

export async function joinCommand(
  code: string,
  options: { provider?: string; model?: string } = {},
): Promise<void> {
  banner()
  nodePreflight()

  const providers = await detectInstalledProviders()
  if (providers.length === 0) {
    console.log(chalk.red('No AI coding providers detected.'))
    console.log()
    console.log(chalk.gray('Your AI tool may not be on PATH. Common fixes:'))
    console.log(chalk.gray('  1. Verify npm global bin is on PATH:'))
    console.log(chalk.white(`     export PATH="$(npm prefix -g)/bin:$PATH"`))
    console.log(chalk.gray('  2. Install an AI coding tool:'))
    console.log(chalk.white('     npm install -g freebuff'))
    console.log(chalk.white('     npm install -g cline'))
    console.log(chalk.white('     npm install -g @charmland/crush'))
    console.log(chalk.white('     npm install -g @earendil-works/pi-coding-agent'))
    console.log(chalk.white('     npm install -g @openai/codex'))
    console.log(chalk.white('     curl -fsSL https://x.ai/cli/install.sh | bash'))
    console.log(chalk.gray('  3. Or run: ') + chalk.white('npx freebuff'))
    console.log()
    process.exit(1)
  }

  const launcherName = await selectProvider(providers, options.provider, 'Choix')

  const launcher = getLauncher(launcherName)
  if (!launcher) {
    console.log(chalk.red(`Provider not found: ${launcherName}`))
    process.exit(1)
  }

  const spinner = ora('Connecting...').start()

  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  try {
    let workdir: string
    let systemPromptPath: string
    let scenarioName: string
    let scenarioBriefing: string | undefined

    if (isCloudSessionCode(code)) {
      const result = await downloadSessionFromCloud(getWorkerUrl(), code, undefined, (stage) => {
        spinner.text = stage
      })
      workdir = result.workdir
      systemPromptPath = result.systemPromptPath
      scenarioName = result.metadata.scenarioName
      scenarioBriefing = result.metadata.briefing
    } else {
      const { host, port } = decodeSessionCode(code)
      const result = await downloadSession(host, port, undefined, (stage) => {
        spinner.text = stage
      })
      workdir = result.workdir
      systemPromptPath = result.systemPromptPath
      scenarioName = result.metadata.scenarioName
      scenarioBriefing = result.metadata.briefing
    }

    spinner.succeed('Workspace ready')
    console.log()
    console.log(chalk.gray(`Scenario: ${chalk.white(scenarioName)}`))
    console.log(chalk.gray(`Workdir:  ${workdir}`))
    console.log()

    // Show the briefing to the candidate before launching the AI provider
    if (scenarioBriefing) {
      console.log(chalk.bold('Interview Briefing:'))
      console.log(chalk.gray('─'.repeat(40)))
      console.log(scenarioBriefing.trim())
      console.log(chalk.gray('─'.repeat(40)))
      console.log()
      console.log(chalk.gray('Press Enter to continue...'))
      await new Promise<void>((resolve) => {
        process.stdin.setRawMode?.(true)
        process.stdin.resume()
        const onData = () => {
          process.stdin.removeListener('data', onData)
          process.stdin.setRawMode?.(false)
          process.stdin.pause()
          resolve()
        }
        process.stdin.on('data', onData)
      })
      console.log()
    }

    // Create a session record
    const session: Session = {
      id: `join-${Date.now().toString(36)}`,
      scenarioName,
      workdir,
      systemPromptPath,
      status: 'running',
      createdAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
      provider: launcherName,
    }
    await saveSession(toStoredSession(session))

    // Loop 5: send heartbeat for cloud sessions. We send one immediately
    // and then refresh periodically for the duration of the session. The
    // worker's initial TTL is 6h; without periodic refreshes, candidates
    // who take longer than that to interact (e.g. long reads of the
    // briefing) would have their session expire mid-test. Every refresh
    // bumps the TTL to 30d.
    if (isCloudSessionCode(code)) {
      const apiCode = code.replace(/^TONTY-/i, '').toLowerCase()
      const workerUrl = getWorkerUrl()
      const sendHeartbeat = () => {
        void sendHeartbeatWithRetry(workerUrl, apiCode)
      }
      sendHeartbeat()
      heartbeatTimer = setInterval(sendHeartbeat, 5 * 60 * 1000)
    }

    console.log(chalk.gray(`Démarrage de ${launcher.displayName}...`))
    console.log()
    const manager = new SessionManager(launcher)

    // Once the AI tool exits we MUST get the score + recording to the relay.
    // Scoring (verify.sh, up to 60s) and the uploads run with a "frozen"
    // screen, and a candidate who presses Ctrl+C again at that point used to
    // kill the CLI before anything was uploaded — leaving the admin dashboard
    // stuck on "check-in". Guard the whole finalization phase so a second
    // Ctrl+C defers the exit until the result is safely uploaded (or has
    // failed all retries).
    let finalizeSignal: NodeJS.Signals | null = null
    const onFinalizeSignal = (sig: NodeJS.Signals) => {
      if (finalizeSignal) return
      finalizeSignal = sig
      console.log()
      console.log(
        chalk.yellow(
          `  ${sig === 'SIGINT' ? 'Ctrl+C' : 'Signal'} reçu — finalisation de la session, envoi du résultat en cours...`,
        ),
      )
    }
    process.on('SIGINT', onFinalizeSignal)
    process.on('SIGTERM', onFinalizeSignal)

    try {
      const result = await manager.launchProvider(session, {} as ScenarioConfig, {
        model: options.model,
      })

      if (heartbeatTimer) clearInterval(heartbeatTimer)

      session.status = 'complete'
      session.completedAt = new Date().toISOString()
      await saveSession(toStoredSession(session))

      // Upload score and recording for cloud sessions
      const sessionData = await loadSession(session.id)
      if (isCloudSessionCode(code) && sessionData?.score) {
        const apiCode = code.replace(/^TONTY-/i, '').toLowerCase()
        const workerUrl = getWorkerUrl()

        // Final heartbeat before upload: guarantees the relay knows the
        // session started (startedAt) even if every periodic heartbeat
        // failed earlier, and bumps the TTL so the uploads can't race an
        // expiry.
        await sendHeartbeatWithRetry(workerUrl, apiCode)

        // Upload score with retry (fire-and-forget failed silently before)
        console.log(chalk.gray('  Envoi du score...'))
        let scoreUploaded = false
        for (let attempt = 0; attempt < 5 && !scoreUploaded; attempt++) {
          try {
            const res = await fetch(`${workerUrl}/sessions/${apiCode}/complete`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(sessionData.score),
              signal: AbortSignal.timeout(20000),
            })
            if (res.ok) {
              scoreUploaded = true
            } else {
              console.log(
                chalk.yellow(`  Score upload attempt ${attempt + 1} failed (${res.status})`),
              )
              if (attempt < 4) await new Promise((r) => setTimeout(r, (attempt + 1) * 2000))
            }
          } catch {
            console.log(chalk.yellow(`  Score upload attempt ${attempt + 1} failed`))
            if (attempt < 4) await new Promise((r) => setTimeout(r, (attempt + 1) * 2000))
          }
        }
        if (!scoreUploaded) {
          console.log(chalk.yellow('  Score upload failed after 5 attempts'))
        }

        // Upload session recording with environment info
        try {
          const { SessionRecorder } = await import('@tonty/core')
          const os = await import('node:os')
          const recordings = await SessionRecorder.list()
          const recId = recordings.find((r: string) => r === session.id || r.startsWith(session.id))
          if (recId) {
            const recording = await SessionRecorder.load(recId)
            const events = recording.getEvents()
            // Truncate per-event AND cap the total payload so the recording
            // stays well under Cloudflare KV's 25MB value limit. Per-event
            // truncation alone is not enough for long chatty TUI sessions —
            // the recording PUT used to fail with a 413/500 and the
            // transcript was silently lost (the score uploads separately, so
            // admins saw a score but no transcript). Keep the tail: the end
            // of the interview is the part that matters.
            const MAX_RECORDING_CHARS = 6 * 1024 * 1024
            const truncated = events.map((e: SessionEvent) => ({
              timestamp: e.timestamp,
              type: e.type,
              data: e.data.slice(0, 2000),
            }))
            let budget = MAX_RECORDING_CHARS
            let start = truncated.length - 1
            for (let i = truncated.length - 1; i >= 0; i--) {
              if (budget - truncated[i]!.data.length < 0) break
              budget -= truncated[i]!.data.length
              start = i
            }
            const eventsForUpload = truncated.slice(start)
            const recordingData = {
              provider: launcherName,
              os: `${os.platform()} ${os.release()} ${os.arch()}`,
              nodeVersion: process.version,
              events: eventsForUpload,
              env: sessionData.env,
              setupLog: sessionData.setupLog,
              compromised: sessionData.compromised,
              status: sessionData.status,
              exitCode: sessionData.exitCode,
              verifyOutput: sessionData.verifyOutput,
            }
            // Upload with retry (44% failure rate observed in production)
            console.log(chalk.gray('  Envoi de l\u2019enregistrement...'))
            let uploaded = false
            for (let attempt = 0; attempt < 5 && !uploaded; attempt++) {
              try {
                const res = await fetch(`${workerUrl}/sessions/${apiCode}/recording`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(recordingData),
                  signal: AbortSignal.timeout(30000),
                })
                if (res.ok) {
                  uploaded = true
                } else {
                  console.log(
                    chalk.yellow(
                      `  Recording upload attempt ${attempt + 1} failed (${res.status})`,
                    ),
                  )
                  if (attempt < 4) await new Promise((r) => setTimeout(r, (attempt + 1) * 2000))
                }
              } catch {
                console.log(chalk.yellow(`  Recording upload attempt ${attempt + 1} failed`))
                if (attempt < 4) await new Promise((r) => setTimeout(r, (attempt + 1) * 2000))
              }
            }
            if (!uploaded) {
              console.log(chalk.yellow('  Recording upload failed after 5 attempts'))
            }
          }
        } catch {
          // Recording upload is best-effort
        }
      }

      if (result.exitCode === 0) {
        console.log(chalk.green('Session completed.'))
      } else {
        console.log(chalk.yellow(`Session ended with exit code ${result.exitCode}.`))
      }
      await displayScore(session.id)
    } finally {
      process.removeListener('SIGINT', onFinalizeSignal)
      process.removeListener('SIGTERM', onFinalizeSignal)
      // If the candidate pressed Ctrl+C during finalization, leave with the
      // conventional exit code once the result has been sent.
      if (finalizeSignal) {
        process.exit(finalizeSignal === 'SIGINT' ? 130 : 143)
      }
    }
  } catch (err) {
    if (heartbeatTimer) clearInterval(heartbeatTimer)
    spinner.fail('Failed to join session')
    handleError(err)
  }
}

export async function sessionsCommand(): Promise<void> {
  banner()
  const sessions = await listSessions()

  if (sessions.length === 0) {
    console.log(chalk.gray('No sessions found.'))
    return
  }

  console.log(chalk.bold('Sessions:'))
  console.log()

  for (const s of sessions) {
    const statusColor =
      s.status === 'running' ? chalk.green : s.status === 'complete' ? chalk.gray : chalk.yellow
    const compromised = s.compromised ? chalk.red(' ⚠') : ''
    console.log(
      `  ${chalk.bold(s.id.padEnd(12))} ${statusColor(s.status.padEnd(10))} ${chalk.gray(s.scenarioName.padEnd(25))} ${s.provider ?? '—'}  ${new Date(s.createdAt).toLocaleString()}${compromised}`,
    )
  }

  console.log()
}

export async function providersCommand(): Promise<void> {
  banner()
  const providers = await detectInstalledProviders()

  console.log(chalk.bold('Detected AI Providers:'))
  console.log()

  if (providers.length === 0) {
    console.log(chalk.yellow('  No providers detected.'))
    console.log()
    console.log(chalk.gray('  Your AI tool may not be on PATH. Common fixes:'))
    console.log(chalk.gray('    • Add npm global bin to PATH:'))
    console.log(chalk.white(`       export PATH="$(npm prefix -g)/bin:$PATH"`))
    console.log(chalk.gray('    • Install one of:'))
    console.log(chalk.white('       npm install -g freebuff'))
    console.log(chalk.white('       npm install -g cline'))
    console.log(chalk.white('       npm install -g @charmland/crush'))
    console.log(chalk.white('       npm install -g @earendil-works/pi-coding-agent'))
    console.log(chalk.white('       npm install -g @openai/codex'))
    console.log(chalk.white('       curl -fsSL https://x.ai/cli/install.sh | bash'))
    console.log(chalk.gray('    • Or use npx (bypasses PATH):'))
    console.log(chalk.white('       npx freebuff'))
  } else {
    for (const p of providers) {
      console.log(
        `  ${chalk.green('✓')} ${chalk.white(p.launcher.displayName)} ${chalk.gray(`(${p.version ?? ''})`)}`,
      )
      console.log(`      ${chalk.gray(p.launcher.pricingHint)}`)
    }
  }

  console.log()
}

/**
 * Robust provider selection.
 *
 * - If `--provider <name>` is given, use it directly (validated against detected list).
 * - If only one provider is detected, auto-pick it.
 * - If multiple are detected, prompt the candidate. The prompt accepts:
 *     • the index (1..N)
 *     • the provider's internal name (e.g. "codex")
 *     • a substring of the display name (e.g. "code" matches "Codex (OpenAI)")
 *   On Enter with no input in a TTY, fall back to the **first detected provider** (Claude Code
 *   by registry order) — the candidate will see which one was picked and can restart with
 *   `--provider`. On non-TTY (CI, piped stdin, SSH with no PTY), fall back to providers[0].
 *
 * This fixes the historical bug where typing "codex" (the package name) at the prompt
 * was parsed as NaN, falling back to providers[0] and launching Claude Code instead.
 */
async function selectProvider(
  providers: DetectedProvider[],
  flagValue: string | undefined,
  promptLabel = 'Pick',
): Promise<string> {
  if (flagValue) {
    const found = providers.find(
      (p) =>
        p.launcher.name === flagValue ||
        p.launcher.displayName.toLowerCase() === flagValue.toLowerCase(),
    )
    if (found) return found.launcher.name
    console.log(
      chalk.yellow(
        `Warning: --provider "${flagValue}" not detected. Available: ${providers.map((p) => p.launcher.displayName).join(', ')}`,
      ),
    )
    console.log()
  }

  if (providers.length === 1) {
    return providers[0]!.launcher.name
  }

  console.log(chalk.bold('Plusieurs outils IA détectés. Choisissez :'))
  console.log()
  for (let i = 0; i < providers.length; i++) {
    const p = providers[i]!
    console.log(
      `  ${chalk.white(`${i + 1}.`)} ${p.launcher.displayName} ${chalk.gray(`(${p.version ?? 'inconnu'})`)}`,
    )
    console.log(`     ${chalk.gray(p.launcher.pricingHint)}`)
  }
  console.log()
  process.stdout.write(chalk.gray(`${promptLabel} [1-${providers.length} or provider name]: `))

  let choice = ''
  if (process.stdin.isTTY) {
    choice = await new Promise<string>((resolve) => {
      const onData = (chunk: Buffer) => {
        const key = chunk.toString('utf-8')
        process.stdin.removeListener('data', onData)
        process.stdin.setRawMode?.(false)
        process.stdin.pause()
        resolve(key.replace(/[\r\n]+/g, '').trim())
      }
      process.stdin.setRawMode?.(true)
      process.stdin.resume()
      process.stdin.on('data', onData)
    })
  }
  process.stdout.write('\n')

  if (!choice) {
    return providers[0]!.launcher.name
  }

  // 1) Index match
  const idx = parseInt(choice, 10) - 1
  if (Number.isFinite(idx) && idx >= 0 && idx < providers.length) {
    return providers[idx]!.launcher.name
  }

  const needle = choice.toLowerCase()
  // 2) Exact internal-name match
  const byName = providers.find((p) => p.launcher.name.toLowerCase() === needle)
  if (byName) return byName.launcher.name
  // 3) Exact display-name match
  const byDisplay = providers.find((p) => p.launcher.displayName.toLowerCase() === needle)
  if (byDisplay) return byDisplay.launcher.name
  // 4) Substring match — prefer internal-name match (more specific), then
  //    display-name. Among multiple substring matches, pick the one whose
  //    matched field is shortest (most specific) so "code" picks Codex
  //    over Claude Code.
  const substringMatches = providers
    .map((p) => {
      const name = p.launcher.name.toLowerCase()
      const display = p.launcher.displayName.toLowerCase()
      const nameIdx = name.indexOf(needle)
      const displayIdx = display.indexOf(needle)
      if (nameIdx === -1 && displayIdx === -1) return null
      // Prefer internal-name hits
      if (nameIdx !== -1) return { p, field: name }
      return { p, field: display }
    })
    .filter((x): x is { p: DetectedProvider; field: string } => x !== null)
    .sort((a, b) => a.field.length - b.field.length)
  if (substringMatches.length > 0) {
    return substringMatches[0]!.p.launcher.name
  }

  console.log(
    chalk.yellow(
      `No match for "${choice}". Falling back to ${providers[0]!.launcher.displayName}.`,
    ),
  )
  return providers[0]!.launcher.name
}

async function displayScore(sessionId: string): Promise<void> {
  const { loadSession } = await import('@tonty/core')
  const session = await loadSession(sessionId)
  if (!session?.score) return

  const { score } = session
  console.log()
  console.log(chalk.bold('Score:'))
  console.log(chalk.gray('─'.repeat(30)))
  console.log(
    chalk.white(`  Result:      ${score.passed ? chalk.green('PASS') : chalk.red('FAIL')}`),
  )
  console.log(chalk.white(`  Duration:    ${formatDuration(score.durationMs)}`))
  console.log(chalk.white(`  Prompts:     ${score.promptCount}`))
  console.log()
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}

interface ErrorWithExtras extends Error {
  code?: string
  hint?: string
}

function handleError(err: unknown): void {
  if (err instanceof Error) {
    const e = err as ErrorWithExtras
    console.log(chalk.red(`Error: ${err.message}`))
    if (e.code) {
      console.log(chalk.gray(`  Code: ${e.code}`))
    }
    if (e.hint) {
      console.log(chalk.gray(e.hint))
    }
    // Stack only shown when verbose is requested via TONTY_DEBUG=1
    if (process.env['TONTY_DEBUG'] === '1' && err.stack) {
      console.log(chalk.gray(err.stack))
    }
    // Common spawn failures get actionable hints
    const msg = err.message.toLowerCase()
    if (msg.includes('posix_spawn') || msg.includes('spawn ')) {
      console.log(
        chalk.gray(
          '  Hint: this is a process spawn error. Try running the same command in a fresh shell, or check that the AI CLI binary is on PATH.',
        ),
      )
      console.log(
        chalk.gray(
          '  If on macOS ARM and using npx, ensure `node` and the target CLI are installed globally (npm i -g <pkg>).',
        ),
      )
    } else if (
      msg.includes('econnreset') ||
      msg.includes('enotfound') ||
      msg.includes('etimedout')
    ) {
      console.log(
        chalk.gray('  Hint: network error. Check your connection and the cloud relay status.'),
      )
    }
  } else {
    console.log(chalk.red(`Error: ${String(err)}`))
  }
  process.exitCode = 1
}
